import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

const PointScoringHistoryChart = ({ h2hData = [], selectedManager = 'Kevin', className = "" }) => {
  // Prepare chart data
  const prepareChartData = () => {
    if (!h2hData.length || !selectedManager) return { chartData: [], medianPF: 0, mostRecentGameID: null };

    // Filter data for selected manager
    const managerData = h2hData.filter(row => row.Manager1 === selectedManager);
    
    if (!managerData.length) return { chartData: [], medianPF: 0, mostRecentGameID: null };

    // Calculate median points for
    const medianPF = managerData.length > 0 ? 
      [...managerData.map(row => row.PF || 0)].sort((a, b) => a - b)[Math.floor(managerData.length / 2)] : 0;

    // Find most recent game
    const mostRecentGameID = Math.max(...managerData.map(row => row.GameID || 0));

    // Prepare data sorted by PF (descending) to match R code reorder behavior
    const chartData = managerData
      .map((game, index) => ({
        gameIndex: index,
        gameID: game.GameID,
        points: game.PF || 0,
        pointsAgainst: game.PA || 0,
        outcome: game.Outcome,
        opponent: game.Manager2,
        yearWeek: game.YearWeek,
        type: game.Type,
        isRecent: game.GameID === mostRecentGameID,
        // Create tooltip data
        tooltipData: {
          opponent: game.Manager2,
          outcome: game.Outcome,
          stakes: game.Type,
          date: game.YearWeek,
          pointsFor: (game.PF || 0).toFixed(1),
          pointsAgainst: (game.PA || 0).toFixed(1)
        }
      }))
      .sort((a, b) => b.points - a.points); // Sort by points descending to match R reorder

    return { chartData, medianPF, mostRecentGameID };
  };

  const { chartData, medianPF, mostRecentGameID } = prepareChartData();

  if (!chartData.length) {
    return (
      <div className={`bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 ${className}`}>
        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">
          <span className="hidden sm:inline">
            {selectedManager}'s point scoring history, with his most recent game highlighted.
          </span>
          <span className="sm:hidden">
            {selectedManager}'s Scoring History
          </span>
        </h3>
        <p className="text-gray-500">No data available for {selectedManager}</p>
      </div>
    );
  }

  // Custom tooltip component with mobile optimization
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload.tooltipData;
      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-xl p-3 sm:p-4 max-w-xs sm:max-w-sm">
          <div className="space-y-1 sm:space-y-1.5">
            <p className="font-semibold text-gray-900 text-sm sm:text-base">Game Details</p>
            
            {/* Mobile-first layout */}
            <div className="grid grid-cols-1 gap-1 text-xs sm:text-sm">
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">vs</span>
                <span className="text-gray-900 font-medium">{data.opponent}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Result</span>
                <span className={`font-semibold ${data.outcome === 'Win' ? 'text-green-600' : 'text-red-600'}`}>
                  {data.outcome}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Points</span>
                <span className="text-gray-900 font-medium">{data.pointsFor} - {data.pointsAgainst}</span>
              </div>
              
              {/* Show less critical info only on larger screens or in expanded view */}
              <div className="hidden sm:flex justify-between">
                <span className="font-medium text-gray-600">Stakes</span>
                <span className="text-gray-900">{data.stakes}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Date</span>
                <span className="text-gray-900 text-xs sm:text-sm">{data.date}</span>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 ${className}`}>
      {/* Title with mobile optimization */}
      <div className="mb-4 sm:mb-6">
        <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 leading-tight">
          <span className="hidden sm:inline">
            {selectedManager}'s point scoring history, with his most recent game highlighted.
          </span>
          <span className="sm:hidden">
            {selectedManager}'s Scoring History
          </span>
        </h3>
        <p className="text-gray-600 text-sm lg:text-base">
          <span className="hidden sm:inline">
            Points scored by {selectedManager} across all games. Median: {medianPF.toFixed(1)} points.
          </span>
          <span className="sm:hidden">
            {chartData.length} games • Median: {medianPF.toFixed(1)} pts
          </span>
        </p>
      </div>

      {/* Chart with responsive height */}
      <div className="h-64 sm:h-80 lg:h-96">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{
              top: 10,
              right: 10,
              left: 10,
              bottom: 10,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis 
              dataKey="gameIndex"
              tick={false}
              axisLine={false}
              tickLine={false}
            />
            <YAxis 
              stroke="#6b7280"
              fontSize={10}
              tickLine={false}
              axisLine={false}
              width={window.innerWidth < 640 ? 35 : 50}
              label={window.innerWidth >= 640 ? { 
                value: `Points scored by ${selectedManager}`, 
                angle: -90, 
                position: 'insideLeft',
                style: { textAnchor: 'middle', fill: '#6b7280', fontSize: '11px' }
              } : undefined}
            />
            <Tooltip content={<CustomTooltip />} />
            
            {/* Median line with responsive styling */}
            <ReferenceLine 
              y={medianPF} 
              stroke="#374151" 
              strokeDasharray="4 4"
              strokeWidth={window.innerWidth < 640 ? 1.5 : 2}
            />
            
            {/* Zero line */}
            <ReferenceLine 
              y={0} 
              stroke="#000000" 
              strokeWidth={window.innerWidth < 640 ? 1.5 : 2}
              strokeOpacity={0.5}
            />

            {/* Bars with custom coloring and responsive width */}
            <Bar 
              dataKey="points"
              maxBarSize={window.innerWidth < 640 ? 8 : 12}
              shape={(props) => {
                const { payload, ...rest } = props;
                
                let fill = "#ef4444"; // Default red for losses
                let fillOpacity = 0.1;
                
                if (payload.isRecent) {
                  fill = "#8b5cf6"; // Purple for most recent
                  fillOpacity = 1;
                } else if (payload.outcome === 'Win') {
                  fill = "#22c55e"; // Green for wins
                  fillOpacity = 0.1;
                }
                
                return (
                  <rect 
                    {...rest} 
                    fill={fill} 
                    fillOpacity={fillOpacity}
                  />
                );
              }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Mobile-optimized legend */}
      <div className="mt-3 sm:mt-4">
        {/* Mobile: 2x2 grid */}
        <div className="grid grid-cols-2 gap-2 sm:hidden text-xs">
          <div className="flex items-center space-x-1">
            <div className="w-3 h-2 bg-green-500 opacity-10 border border-green-500 flex-shrink-0"></div>
            <span className="text-gray-700 truncate">Wins</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-2 bg-red-500 opacity-10 border border-red-500 flex-shrink-0"></div>
            <span className="text-gray-700 truncate">Losses</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-2 bg-purple-600 flex-shrink-0"></div>
            <span className="text-gray-700 truncate">Recent</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-0.5 bg-gray-700 border-dashed flex-shrink-0"></div>
            <span className="text-gray-700 truncate">Med. {medianPF.toFixed(0)}</span>
          </div>
        </div>

        {/* Desktop: horizontal layout */}
        <div className="hidden sm:flex items-center justify-center space-x-4 lg:space-x-6 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-3 bg-green-500 opacity-10 border border-green-500"></div>
            <span className="text-gray-700">Wins</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-3 bg-red-500 opacity-10 border border-red-500"></div>
            <span className="text-gray-700">Losses</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-3 bg-purple-600"></div>
            <span className="text-gray-700">Most Recent Game</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-0.5 bg-gray-700 border-dashed"></div>
            <span className="text-gray-700">Median ({medianPF.toFixed(1)})</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PointScoringHistoryChart;